import { Controller } from "@hotwired/stimulus"
import { debounce } from 'lodash'

const LOADING_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" aria-hidden="true" class="size-5 fill-blue-700 motion-safe:animate-spin dark:fill-blue-600">
    <path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity=".25" />
    <path d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z" />
</svg>`

const WARNING_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
  <line x1="12" y1="9" x2="12" y2="13"></line>
  <line x1="12" y1="17" x2="12.01" y2="17"></line>
</svg>`

const INFO_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <circle cx="12" cy="12" r="10"></circle>
  <line x1="12" y1="16" x2="12" y2="12"></line>
  <line x1="12" y1="8" x2="12.01" y2="8"></line>
</svg>`

const ARROW_CIRCLE_RIGHT_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" viewBox="0 0 256 256" class="inline-block align-text-bottom h-6 w-6">
  <path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm45.66,109.66-32,32a8,8,0,0,1-11.32-11.32L148.69,136H88a8,8,0,0,1,0-16h60.69l-18.35-18.34a8,8,0,0,1,11.32-11.32l32,32A8,8,0,0,1,173.66,133.66Z"></path>
</svg>`

export default class extends Controller {
  static targets = ['form', 'suggestions', 'wayid', 'name', 'from', 'distance', 'kmLabel']
  static values = {
    poisCache: { type: Object, default: {} }
  }

  connect() {
    this.initializeKmLabel()
    this.debouncedDistanceSearch = debounce(() => this.performDistanceSearch(), 1000)
    this.initializeFromUrl()

    const input = document.querySelector('#location-input')
    
    if (input) {
      input.addEventListener('focus', () => {
        if (window.matchMedia('(max-width: 768px)').matches) {
          console.log("Input focused on mobile, scrolling to view")
          input.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
          })
        }
      })
    }

    if (this.hasNameTarget) {
      this.nameTarget.addEventListener('focus', () => {
        if (window.matchMedia('(max-width: 768px)').matches) {
          setTimeout(() => {
            const element = this.nameTarget
            const headerOffset = 20
            const elementPosition = element.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset
            
            window.scrollTo({
              top: offsetPosition,
              behavior: "smooth"
            })
          }, 100)
        }
      })
    }
  }

  initializeFromUrl() {
    const path = window.location.pathname
    
    // Récupérer from de l'URL
    const fromMatch = path.match(/\/from\/(\d+)/)
    if (fromMatch && this.hasFromTarget) {
      this.fromTarget.value = fromMatch[1]
    }
    
    // Récupérer distance de l'URL
    const distanceMatch = path.match(/\/distance\/(\d+)/)
    if (distanceMatch && this.hasDistanceTarget) {
      this.distanceTarget.value = distanceMatch[1]
      this.toggleKmLabel()
    }

    // Récupérer track et trace de l'URL
    const trackMatch = path.match(/\/track\/(\d+)/)
    if (trackMatch) {
      this.track = trackMatch[1]
    }

    const traceMatch = path.match(/\/trace\/(\d+)/)
    if (traceMatch) {
      this.trace = traceMatch[1]
    }
  }

  initializeKmLabel() {
    if (this.hasDistanceTarget && this.hasKmLabelTarget) {
      this.toggleKmLabel()
    }
  }

  onDistanceFocus(event) {
    event.target.value = ''
    this.toggleKmLabel()
  }

  toggleKmLabel() {
    if (this.hasDistanceTarget && this.hasKmLabelTarget) {
      const showLabel = this.distanceTarget.value !== ''
      this.kmLabelTarget.classList.toggle('hidden', !showLabel)
    }
  }

  distanceChanged(event) {
    this.toggleKmLabel()
    const distanceValue = event.target.value
    const distanceNumber = parseInt(distanceValue)

    if (distanceValue === '') {
      this.clearSuggestions()
      return
    }

    if (distanceNumber > 40) {
      this.showMessage("La distance de recherche est limtée à 40 km", "warning")
      this.validateDistance(event.target)
      this.debouncedDistanceSearch()
    } else if (distanceNumber >= 0) {
      // Lancer la recherche pour toute valeur valide, y compris 0
      this.showLoadingIndicator()
      this.debouncedDistanceSearch()
    } else {
      this.clearSuggestions()
    }
  }

  validateDistance(input) {
    input.value = Math.min(parseInt(input.value) || 0, 40)
  }

  buildUrl(from, distance) {
    const baseUrl = this.formTarget.action.split('/hebergements')[0] + '/hebergements'
    const urlParts = []
    
    if (from) urlParts.push(`from/${from}`)
    if (distance && parseInt(distance) > 0) urlParts.push(`distance/${distance}`)
    if (this.track) urlParts.push(`track/${this.track}`)
    if (this.trace) urlParts.push(`trace/${this.trace}`)
    
    return urlParts.length > 0 ? `${baseUrl}/${urlParts.join('/')}` : baseUrl
  }

  selectPoi(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.poiId
    const name = event.currentTarget.dataset.poiName
    this.suggestionsTarget.innerHTML = ''
    this.nameTarget.value = name
    this.fromTarget.value = id

    const finalUrl = this.buildUrl(id, this.distanceTarget?.value)
    Turbo.visit(finalUrl, { action: "replace" })
  }

  performDistanceSearch = () => {
    const distance = parseInt(this.distanceTarget.value)
    if (distance >= 0 && distance <= 40) {
      const finalUrl = this.buildUrl(this.fromTarget.value, distance)
      Turbo.visit(finalUrl, { action: "replace" })
    }
  }

  formChanged(event) {
    event.preventDefault()
    const finalUrl = this.buildUrl(this.fromTarget.value, this.distanceTarget?.value)
    Turbo.visit(finalUrl, { action: "replace" })
  }

  suggestions(event) {
    // Reset la distance quand on clique dans le champ de point de départ
    if (this.hasDistanceTarget) {
      this.distanceTarget.value = ''
      this.toggleKmLabel()
    }

    const query = event.target.value
    if (query.length > 2) {
      this.searchSuggestions(query)
    } else {
      this.suggestionsTarget.innerHTML = ''
    }
  }

  async searchSuggestions(query) {
    const wayId = this.wayidTarget.value
    const searchQuery = query.toLowerCase()
    
    // Vérifier le cache
    if (this.poisCacheValue[wayId]) {
      console.log("🔍 Using cached POIs")
      const filteredPois = this.poisCacheValue[wayId].filter(poi => 
        (poi.name || '').toLowerCase().includes(searchQuery) ||
        (poi.city || '').toLowerCase().includes(searchQuery)
      ).slice(0, 5)
      this.showSuggestions(filteredPois)
      return
    }

    this.showLoadingIndicator()

    try {
      const response = await fetch(`/api/pois_cached.json?way_id=${wayId}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Erreur réseau')
      }
      
      const data = await response.json()
      this.poisCacheValue[wayId] = data.results

      const filteredPois = data.results.filter(poi => 
        (poi.name || '').toLowerCase().includes(searchQuery) ||
        (poi.city || '').toLowerCase().includes(searchQuery)
      ).slice(0, 5)

      this.showSuggestions(filteredPois)

    } catch (error) {
      this.showErrorMessage(error.message)
    }
  }

  showSuggestions(pois) {
    if (pois.length === 0) {
      this.showMessage("Aucun résultat trouvé. Essayez de modifier votre recherche.")
    } else {
      const suggestions = pois
        .map(poi => {
          let { name, zip_code, city, id } = poi
          return `
            <li class="cursor-pointer group bg-white px-6 py-4 results w-full hover:bg-yellow-300 border-b-2 font-semibold text-blue flex justify-between"
                data-poi-name="${name}" 
                data-poi-id="${id}" 
                data-action="click->cached-search#selectPoi">
                <div>
                    ${name}<br>
                    <span class="font-normal text-blue-500">${zip_code} ${city}</span>
                </div>
                <div class="text-xs font-light hidden md:group-hover:flex md:group-hover:items-center md:group-hover:space-x-1 group-hover:underline">
                    <span>Sélectionner</span>
                    ${ARROW_CIRCLE_RIGHT_ICON}
                </div>
            </li>`
        })
        .join('')
      this.suggestionsTarget.innerHTML = suggestions
    }
  }

  clearSuggestions() {
    this.suggestionsTarget.innerHTML = ''
  }

  showLoadingIndicator() {
    this.suggestionsTarget.innerHTML = `
      <li class="bg-white px-6 py-4 text-blue flex items-center">
        ${LOADING_ICON}
        <span class="ml-2">Recherche en cours...</span>
      </li>
    `
  }

  hideLoadingIndicator() {
  }

  showErrorMessage(message = "Une erreur s'est produite. Veuillez réessayer ultérieurement.") {
    this.showMessage(message, "warning")
  }

  showMessage(message, type = "info") {
    let icon = INFO_ICON
    let className = "text-blue-500"

    if (type === "warning") {
      icon = WARNING_ICON
      className = "text-red-600"
    }

    this.suggestionsTarget.innerHTML = `
      <li class="bg-white px-6 py-4 flex items-center">
        ${icon}
        <p class="font-semibold ${className} ml-2">${message}</p>
      </li>
    `
  }
} 
